import React, { useEffect, useState } from 'react';
import { Save, BarChart3, Newspaper, Sparkles, Quote, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import UploadButton from './UploadButton';

// Default values shown in placeholders. If admin saves empty, frontend uses fallback.
const DEFAULTS = {
  // Etkimiz
  home_impact_kicker: 'Etkimiz',
  home_impact_title: 'Rakamlarla <em>değişim</em><br />hikâyelerimiz.',
  home_impact_text: "1992'den bu yana Türkiye'nin her köşesindeki çocuk ve gencimizle buluştuk. Her bir rakam; bir hayal, bir diploma, bir gelecektir.",
  home_stat_1_value: '12500', home_stat_1_suffix: '+', home_stat_1_label: 'Bursiyer', home_stat_1_sub: 'Lise & Üniversite öğrencisi',
  home_stat_2_value: '33', home_stat_2_suffix: '', home_stat_2_label: 'Yıl', home_stat_2_sub: 'Kesintisiz hizmet',
  home_stat_3_value: '120', home_stat_3_suffix: '+', home_stat_3_label: 'Proje', home_stat_3_sub: 'Eğitim & Kültür',
  home_stat_4_value: '500', home_stat_4_suffix: '+', home_stat_4_label: 'Gönüllü', home_stat_4_sub: 'Aktif çalışma topluluğu',
  // Hikayemiz
  home_story_kicker: 'Hikâyemiz',
  home_story_title: 'Bir çocuğun <em>hayatına</em> dokunmak.',
  home_story_text: '21. Yüzyıl Eğitim ve Kültür Vakfı; 1992 yılından bu yana çocuklarımız ve gençlerimiz için eğitimde fırsat eşitliği yaratmak üzere kurulmuştur.',
  home_story_image: '',
  home_story_cta_label: 'Vakfımızı Tanıyın',
  home_story_cta_link: '/kurumsal',
  // Programlar
  home_programs_kicker: 'Programlarımız',
  home_programs_title: 'Eğitim, sanat, <em>topluluk</em>.',
  home_programs_text: 'Bursiyerlerimize yalnızca maddi destek değil; akademik mentörlük, sanat ve kültür buluşmaları ve gönüllü topluluğu sağlıyoruz.',
  // Hikaye/Quote
  home_quote_text: 'YEKÜV bursu, sadece okul masraflarımı karşılayan bir burs değildi. Beni anlayan, dinleyen, akademik yolculuğumda elimden tutan bir aileyle tanıştırdı.',
  home_quote_author: 'Elif K., Tıp Fakültesi mezunu',
  // Gönüllü CTA
  home_volunteer_kicker: 'Gönüllü Ol',
  home_volunteer_title: 'Birlikte daha güçlüyüz.',
  home_volunteer_text: 'Mentörlük, etkinlik organizasyonu, dijital iletişim alanlarında aramıza katılın.',
};

const SECTIONS = [
  {
    key: 'impact', label: 'Etkimiz (KPI Bölümü)', icon: BarChart3,
    description: 'Anasayfada navy zemin üzerindeki "Rakamlarla değişim hikâyelerimiz" bölümü ve 4 KPI kartı.',
    fields: [
      { key: 'home_impact_kicker', label: 'Üst Etiket', short: true },
      { key: 'home_impact_title', label: 'Ana Başlık (HTML kabul eder)', long: true, help: '<em>...</em> ile italik vurgu ekleyebilirsiniz' },
      { key: 'home_impact_text', label: 'Açıklama Paragrafı', long: true },
      { divider: '4 KPI Kartı' },
      { key: 'home_stat_1_value', label: 'KPI 1 — Sayı', short: true, w: 'sm' },
      { key: 'home_stat_1_suffix', label: 'Sonek', short: true, w: 'xs', placeholder: '+ veya % vb.' },
      { key: 'home_stat_1_label', label: 'Etiket', short: true },
      { key: 'home_stat_1_sub', label: 'Alt Açıklama', short: true },
      { key: 'home_stat_2_value', label: 'KPI 2 — Sayı', short: true, w: 'sm' },
      { key: 'home_stat_2_suffix', label: 'Sonek', short: true, w: 'xs' },
      { key: 'home_stat_2_label', label: 'Etiket', short: true },
      { key: 'home_stat_2_sub', label: 'Alt Açıklama', short: true },
      { key: 'home_stat_3_value', label: 'KPI 3 — Sayı', short: true, w: 'sm' },
      { key: 'home_stat_3_suffix', label: 'Sonek', short: true, w: 'xs' },
      { key: 'home_stat_3_label', label: 'Etiket', short: true },
      { key: 'home_stat_3_sub', label: 'Alt Açıklama', short: true },
      { key: 'home_stat_4_value', label: 'KPI 4 — Sayı', short: true, w: 'sm' },
      { key: 'home_stat_4_suffix', label: 'Sonek', short: true, w: 'xs' },
      { key: 'home_stat_4_label', label: 'Etiket', short: true },
      { key: 'home_stat_4_sub', label: 'Alt Açıklama', short: true },
    ],
  },
  {
    key: 'story', label: 'Hikâyemiz Bölümü', icon: Sparkles,
    description: 'Anasayfa "Hikâyemiz" / "Tanışın" bölümü — solda görsel, sağda başlık ve buton.',
    fields: [
      { key: 'home_story_kicker', label: 'Üst Etiket', short: true },
      { key: 'home_story_title', label: 'Ana Başlık', long: true, help: '<em>...</em> destekler' },
      { key: 'home_story_text', label: 'Açıklama', long: true },
      { key: 'home_story_image', label: 'Görsel URL', short: true, image: true },
      { key: 'home_story_cta_label', label: 'Buton Metni', short: true },
      { key: 'home_story_cta_link', label: 'Buton Linki', short: true },
    ],
  },
  {
    key: 'programs', label: 'Programlarımız Bölümü', icon: Newspaper,
    description: 'Anasayfa "Eğitim, sanat, topluluk" bölümünün başlığı ve açıklaması.',
    fields: [
      { key: 'home_programs_kicker', label: 'Üst Etiket', short: true },
      { key: 'home_programs_title', label: 'Ana Başlık', long: true },
      { key: 'home_programs_text', label: 'Açıklama', long: true },
    ],
  },
  {
    key: 'quote', label: 'Bursiyer Alıntısı', icon: Quote,
    description: 'Anasayfada büyük tırnak işaretli alıntı bölümü.',
    fields: [
      { key: 'home_quote_text', label: 'Alıntı Metni', long: true },
      { key: 'home_quote_author', label: 'Alıntı Sahibi', short: true, placeholder: 'Elif K., Tıp Fakültesi mezunu' },
    ],
  },
  {
    key: 'volunteer', label: 'Gönüllü CTA', icon: Heart,
    description: 'Anasayfa altındaki "Gönüllü ol" çağrı kartı.',
    fields: [
      { key: 'home_volunteer_kicker', label: 'Üst Etiket', short: true },
      { key: 'home_volunteer_title', label: 'Başlık', long: true },
      { key: 'home_volunteer_text', label: 'Açıklama', long: true },
    ],
  },
];

export default function AdminHomepage() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState({});
  const [active, setActive] = useState('impact');
  const [dirtyKeys, setDirtyKeys] = useState(new Set());

  useEffect(() => {
    // Load saved settings AND merge with DEFAULTS so form fields show the
    // actual live-site value (either the admin's saved override or the built-in default).
    api.adminListSettings().then(list => {
      const m = {};
      list.forEach(s => { m[s.key] = s.value; });
      // Prefill any missing key with its default so the input shows the current live value.
      Object.keys(DEFAULTS).forEach(k => {
        if (!m[k]) m[k] = DEFAULTS[k];
      });
      setValues(m);
    }).catch(() => {});
  }, []);

  const setField = (key, val) => {
    setValues(s => ({ ...s, [key]: val }));
    setDirtyKeys(s => new Set(s).add(key));
  };

  const saveSection = async (section) => {
    setSaving(s => ({ ...s, [section.key]: true }));
    try {
      const changed = section.fields.filter(f => !f.divider && dirtyKeys.has(f.key));
      const toSave = changed.length ? changed : section.fields.filter(f => !f.divider);
      await Promise.all(toSave.map(f => api.adminUpsertSetting(f.key, values[f.key] || '')));
      // Clear dirty flags for just-saved keys
      setDirtyKeys(s => {
        const n = new Set(s);
        toSave.forEach(f => n.delete(f.key));
        return n;
      });
      toast.success(`${section.label} kaydedildi (${toSave.length} alan)`);
    } catch {
      toast.error('Kaydedilemedi');
    } finally { setSaving(s => ({ ...s, [section.key]: false })); }
  };

  return (
    <div data-testid="admin-homepage">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Anasayfa</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Anasayfa Yönetimi</h1>
        <p className="mt-2 text-ink-400 text-sm">Anasayfadaki bölümlerin başlık, metin, sayı ve görsellerini buradan düzenleyin. <strong>Hero Slider</strong> ayrı menüden yönetilir.</p>
      </div>

      <div className="space-y-4">
        {SECTIONS.map(section => {
          const Icon = section.icon;
          const isOpen = active === section.key;
          return (
            <div key={section.key} className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
              <button onClick={() => setActive(isOpen ? '' : section.key)} className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-cream-100 transition-colors" data-testid={`section-${section.key}`}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-ink-50 text-ink-600 grid place-items-center"><Icon className="w-5 h-5" /></div>
                  <div>
                    <div className="font-display text-xl">{section.label}</div>
                    <div className="text-xs text-ink-400 mt-0.5">{section.description}</div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-ink-400" /> : <ChevronDown className="w-5 h-5 text-ink-400" />}
              </button>

              {isOpen && (
                <div className="px-6 pb-6 border-t border-ink-100 pt-5">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    {section.fields.map((f, idx) => {
                      if (f.divider) {
                        return <div key={idx} className="col-span-full mt-2 mb-1 text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">{f.divider}</div>;
                      }
                      const colSpan = f.w === 'xs' ? 'md:col-span-2' : f.w === 'sm' ? 'md:col-span-3' : f.long ? 'md:col-span-12' : 'md:col-span-6';
                      return (
                        <div key={f.key} className={colSpan}>
                          <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold flex items-center gap-2">
                            {f.label}
                            {dirtyKeys.has(f.key) && <span className="text-[9px] text-coral-300 font-bold">● değişti</span>}
                          </label>
                          {f.help && <div className="text-[10px] text-ink-400 mt-0.5">{f.help}</div>}
                          {f.long ? (
                            <textarea
                              value={values[f.key] || ''}
                              onChange={e => setField(f.key, e.target.value)}
                              placeholder={f.placeholder || DEFAULTS[f.key] || ''}
                              rows={3}
                              data-testid={`field-${f.key}`}
                              className="mt-1.5 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 py-2 text-sm outline-none resize-none"
                            />
                          ) : f.image ? (
                            <div className="mt-1.5 flex gap-2 items-start">
                              {values[f.key] ? (
                                <a href={values[f.key]} target="_blank" rel="noopener noreferrer" className="w-16 h-16 shrink-0 rounded-lg overflow-hidden border border-ink-100 bg-cream-200 grid place-items-center" title="Görseli aç">
                                  <img src={values[f.key]} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                </a>
                              ) : (
                                <div className="w-16 h-16 shrink-0 rounded-lg border border-dashed border-ink-200 bg-cream-200 grid place-items-center text-[9px] text-ink-400 text-center leading-tight px-1">
                                  Görsel<br />yok
                                </div>
                              )}
                              <div className="flex-1 flex flex-col gap-2">
                                <input value={values[f.key] || ''} onChange={e => setField(f.key, e.target.value)} placeholder={f.placeholder || '/uploads/foo.jpg veya https://...'} className="w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-xs outline-none font-mono" />
                                <UploadButton onUploaded={url => setField(f.key, url)} label="Bilgisayardan yükle" />
                              </div>
                            </div>
                          ) : (
                            <input
                              value={values[f.key] || ''}
                              onChange={e => setField(f.key, e.target.value)}
                              placeholder={f.placeholder || DEFAULTS[f.key] || ''}
                              data-testid={`field-${f.key}`}
                              className="mt-1.5 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-5 flex justify-end">
                    <button onClick={() => saveSection(section)} disabled={saving[section.key]} className="bg-gold text-ink-700 rounded-full px-6 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2 disabled:opacity-60">
                      <Save className="w-4 h-4" /> {saving[section.key] ? 'Kaydediliyor...' : 'Bu Bölümü Kaydet'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
